import React, { useEffect, useRef, useState } from 'react';
import { useMeetingManager } from 'amazon-chime-sdk-component-library-react';
import { StyledWrapper } from './Styled';
import { useNavigation } from '../../providers/NavigationProvider';
import { useReactMediaRecorder } from 'react-media-recorder';
import { useAppState } from '../../providers/AppStateProvider';
import { 
  fetchAudioMessages,
  removeAudioMessage,
  passAudioMessage
} from '../../utils/api';

const LiveIndoTranscription = () => {
  const meetingManager = useMeetingManager();
  const { showIndoCaption } = useNavigation();
  const [isRecordingStarted, setIsRecordingStarted] = useState(false);
  const captionEndRef = useRef<null | HTMLDivElement>(null);
  const [attendeeId, setAttendeeId] = useState<string>("");
  const volumeStoppedTimeout = useRef<NodeJS.Timeout | null>(null);
  const lastVolumeUpdateTime = useRef<number | null>(null);
  const SILENCE_DURATION = 500;
  const { meetingId } = useAppState();
  const [partialTranscript, setPartialTranscript] = useState<string>('');

  const { startRecording, stopRecording, mediaBlobUrl } = useReactMediaRecorder({
    audio: true,
    onStop: async (blobUrl: string, blob: Blob) => {
      console.log('Recording stopped:', blobUrl, blob);
      try {
        const base64Audio = await convertBlobToBase64(blob);
        console.log('Base64 Audio:', base64Audio);
        await passAudioMessage(base64Audio, meetingId);
        const pollingInterval = 2000;
        let isTranscriptionReceived = false;
        while (!isTranscriptionReceived) {
          const messageInfo = await fetchAudioMessages();
          console.log(messageInfo);
          if (messageInfo.length > 0 && messageInfo[0].body) {
            const transcription = messageInfo[0].body || '';
            console.log('Transcription:', transcription);
            setPartialTranscript(transcription);
            isTranscriptionReceived = true;
            await removeAudioMessage(messageInfo[0].receiptHandle);
          } else {
            console.log('No transcription yet, retrying...');
            await new Promise(resolve => setTimeout(resolve, pollingInterval));
          }
        }
      } catch (error) {
        console.error('Error during Base64 conversion or sending:', error);
      }
    }
  });

  const handleStartRecording = async () => {
    startRecording();
    setIsRecordingStarted(true);
    console.log('Start recording');
  };

  const handleStopRecording = async () => {
    stopRecording();
    setIsRecordingStarted(false);
    console.log('Stop recording');
  };

  const convertBlobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Data = reader.result?.toString().split(',')[1];
        resolve(base64Data || '');
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(blob);
    });
  };

  const volumeHandler = (attendeeId: string, volume: number | null, muted: boolean | null, signalStrength: number | null) => {
    if (muted) return;

    const currentTime = Date.now();
    if (volume && volume > 0.1  && showIndoCaption) {
      if(!isRecordingStarted){
        handleStartRecording();
      }
      if (volumeStoppedTimeout.current) {
        clearTimeout(volumeStoppedTimeout.current);
        volumeStoppedTimeout.current = null;
      }
      lastVolumeUpdateTime.current = currentTime;
    } else {
      if(isRecordingStarted){
        if (!volumeStoppedTimeout.current) {
          volumeStoppedTimeout.current = setTimeout(() => {
            const timeSinceLastVolume = Date.now() - (lastVolumeUpdateTime.current || 0);
            if (timeSinceLastVolume >= SILENCE_DURATION && isRecordingStarted) {
              handleStopRecording();
            }
          }, SILENCE_DURATION);
        }
      }
      
    }
    
  };

  useEffect(() => {
    const audioVideo = meetingManager.audioVideo;
    const attendeeIdFromConfig = meetingManager.meetingSessionConfiguration?.credentials?.attendeeId;

    if (!audioVideo) return;

    if (attendeeIdFromConfig) {
      setAttendeeId(attendeeIdFromConfig);
    } else {
      console.error("Attendee ID not found in the meeting session configuration");
    }
    
    audioVideo.realtimeSubscribeToVolumeIndicator(attendeeId, volumeHandler);

    return () => {
      audioVideo.realtimeUnsubscribeFromVolumeIndicator(attendeeId, volumeHandler);
      if (volumeStoppedTimeout.current) {
        clearTimeout(volumeStoppedTimeout.current);
      }
      if (isRecordingStarted) {
        handleStopRecording();
      }
    };
  }, [meetingManager, isRecordingStarted, showIndoCaption]);

  useEffect(() => {
    captionEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  });

  return (
    <StyledWrapper active={!!showIndoCaption}>
      <div className="caption-container">
      {partialTranscript && <span className="partial-transcript">{partialTranscript}</span>}
      <div ref={captionEndRef} />
      </div>
    </StyledWrapper>
  );
};

export default LiveIndoTranscription;